import React from "react";

import { Link } from "react-router-dom";
import { Eye, FileClock, MoreHorizontal, Plus, Send } from "lucide-react";
import { Dropdown, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  formatVersion,
  getDocumentContent,
  getDocumentTitle,
  humanize,
  stripHtmlForPreview,
} from "./cmsDocumentUtils";
import { formatDateTime } from "../../hooks/formatDateTime";

export const Badge = ({ value, kind = "status" }) => {
  const { t } = useTranslation();
  return (
  <span
    className={`cmsd-badge cmsd-badge--${kind} cmsd-badge--${String(value || "").toLowerCase()}`}
  >
    {kind === "language" ? String(value || "—").toUpperCase() : t(`cmsDocuments.values.${String(value).toLowerCase()}`, { defaultValue: humanize(value) })}
  </span>
  );
};

export const PageHeader = ({ title, description, children }) => {
  const { t } = useTranslation();
  return (
  <div className="cmsd-header">
    <div>
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
    <div className="cmsd-header__actions">{children}</div>
  </div>
  );
};

export const DocumentActions = ({ item, onPublish }) => {
  const { t } = useTranslation();
  return (
  <Dropdown align="end">
    <Dropdown.Toggle
      className="cmsd-icon-button"
      aria-label={t("cmsDocuments.actions.forDocument", { title: item.title })}
    >
      <MoreHorizontal size={19} />
    </Dropdown.Toggle>
    <Dropdown.Menu>
      <Dropdown.Item as={Link} to={`/cms/documents/${item.id}`}>
        <Eye size={15} /> {t("common.view")}
      </Dropdown.Item>
      <Dropdown.Item as={Link} to={`/cms/documents/${item.id}/versions`}>
        <FileClock size={15} /> {t("cmsDocuments.actions.viewAllVersions")}
      </Dropdown.Item>
      {item.status !== "DRAFT" && (
        <Dropdown.Item as={Link} to={`/cms/documents/${item.id}/new-version`}>
          <Plus size={15} /> {t("cmsDocuments.actions.addNewVersion")}
        </Dropdown.Item>
      )}
      {item.status === "DRAFT" && (
        <Dropdown.Item onClick={() => onPublish(item)}>
          <Send size={15} /> {t("cmsDocuments.actions.publish")}
        </Dropdown.Item>
      )}
    </Dropdown.Menu>
  </Dropdown>
  );
};

export const DocumentStatusToggle = ({ item, busy = false, onToggle }) => {
  const { t } = useTranslation();
  const canToggle = item.status === "PUBLISHED" || item.status === "ARCHIVED";
  if (!canToggle) return <Badge value={item.status} />;
  const active = item.status === "PUBLISHED";

  return (
    <div className="cmsd-status-control">
      <Badge value={item.status} />
      <label
        className="cmsd-status-toggle"
        title={t(active ? "cmsDocuments.actions.deactivateDocument" : "cmsDocuments.actions.activateDocument")}
      >
        <input
          type="checkbox"
          role="switch"
          aria-label={t(active ? "cmsDocuments.actions.deactivateNamed" : "cmsDocuments.actions.activateNamed", { title: getDocumentTitle(item) })}
          checked={active}
          disabled={busy}
          onChange={() => onToggle(item)}
        />
        <span aria-hidden="true" />
        <small>{t(active ? "common.active" : "common.inactive")}</small>
      </label>
    </div>
  );
};

export const LanguageTabs = ({ value, onChange, className = "" }) => {
  const { t } = useTranslation();
  return (
  <div
    className={`cmsd-view-language-tabs ${className}`}
    role="tablist"
    aria-label={t("cmsDocuments.shared.documentLanguage")}
  >
    {["EN", "NL"].map((language) => (
      <button
        key={language}
        type="button"
        role="tab"
        aria-selected={value === language}
        className={value === language ? "active" : ""}
        onClick={() => onChange(language)}
      >
        <span>{language}</span>
        {t(language === "EN" ? "cmsContent.languages.english" : "cmsContent.languages.dutch")}
      </button>
    ))}
  </div>
  );
};

export const DocumentTable = ({
  documents,
  actions,
  language = "EN",
  togglingId,
}) => {
  const { t, i18n } = useTranslation();
  const label = (key) => t(`cmsDocuments.table.${key}`);
  return (
  <>
    <div className="cmsd-table-wrap">
      <table className="cmsd-table">
        <thead>
          <tr>
            <th>{label("document")}</th><th>{label("type")}</th><th>{label("visibility")}</th>
            <th>{label("contentPreview")}</th><th>{label("version")}</th><th>{label("status")}</th>
            <th>{label("createdAt")}</th><th>{label("publishedAt")}</th><th aria-label={t("common.actions")} />
          </tr>
        </thead>
        <tbody>
          {documents.map((item) => (
            <tr key={item.id}>
              <td>
                <Link
                  className="cmsd-title-link"
                  to={`/cms/documents/${item.id}`}
                >
                  {getDocumentTitle(item, language, t("cmsDocuments.shared.untitled"))}
                </Link>
              </td>
              <td>
                <span className="cmsd-type">{humanize(item.type)}</span>
              </td>
              <td>
                <Badge value={item.visibility} kind="visibility" />
              </td>
              <td className="cmsd-preview-cell">
                {stripHtmlForPreview(getDocumentContent(item, language), 110, t("cmsDocuments.shared.noContent"))}
              </td>
              <td className="cmsd-version">{formatVersion(item.version)}</td>
              <td>
                <DocumentStatusToggle
                  item={item}
                  busy={togglingId === item.id}
                  onToggle={actions.onToggle}
                />
              </td>
              <td>{formatDateTime(item.createdAt, i18n, false)}</td>
              <td>{formatDateTime(item.publishedAt, i18n, false)}</td>
              <td>
                <DocumentActions item={item} {...actions} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="cmsd-mobile-list">
      {documents.map((item) => (
        <article className="cmsd-mobile-card" key={item.id}>
          <div className="d-flex justify-content-between">
            <div>
              <Link
                className="cmsd-title-link"
                to={`/cms/documents/${item.id}`}
              >
                {getDocumentTitle(item, language, t("cmsDocuments.shared.untitled"))}
              </Link>
              <div className="cmsd-type mt-1">{humanize(item.type)}</div>
            </div>
            <DocumentActions item={item} {...actions} />
          </div>
          <p className="cmsd-mobile-preview">
            {stripHtmlForPreview(getDocumentContent(item, language), 150, t("cmsDocuments.shared.noContent"))}
          </p>
          <div className="d-flex gap-2 flex-wrap my-3">
            <Badge value={item.visibility} kind="visibility" />
            <DocumentStatusToggle
              item={item}
              busy={togglingId === item.id}
              onToggle={actions.onToggle}
            />
          </div>
          <div className="cmsd-card-meta">
            <span>{formatVersion(item.version)}</span>
            <span>{formatDateTime(item.createdAt, i18n, false)}</span>
          </div>
        </article>
      ))}
    </div>
  </>
  );
};

export const TableSkeleton = () => (
  <div className="cmsd-card">
    <div className="cmsd-skeleton-head" />
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="cmsd-skeleton-row" key={i} />
    ))}
  </div>
);

export const ConfirmModal = ({ action, item, busy, onClose, onConfirm }) => {
  const { t } = useTranslation();
  if (!action || !item) return null;
  return (
    <Modal show centered onHide={busy ? undefined : onClose}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{t("cmsDocuments.confirmPublish.title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {t("cmsDocuments.confirmPublish.body", { version: formatVersion(item.version), title: item.title })}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-light" disabled={busy} onClick={onClose}>
          {t("common.cancel")}
        </button>
        <button
          className="btn cmsd-primary"
          disabled={busy}
          onClick={onConfirm}
        >
          {busy && <Spinner size="sm" className="me-2" />}{t("cmsDocuments.actions.publishVersion")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
